import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {propTypes, reduxForm, Field} from 'redux-form';
import {connect} from 'react-redux';
import compose from 'recompose/compose';
import {
    email,
    translate,
    userLogin as userLoginAction
} from 'admin-on-rest';
import axios from 'axios';
import Config from "./Fuse/messages";
import TokenCheck from './Fuse/components/TokenCheck';
import {clearUser} from './authClient';
import {isLocalhost, removeSearchQuery} from './Fuse/shared/serverFunctions';
import {
    createCodeVerifier,
    decodeJWTToken,
    generateCodeChallenge,
    getKeycloakAuthUrl,
    getKeycloakLogoutUrl,
    getKeycloakTokenUrl
} from './Fuse/shared/tokenFunctions';
import LoginLogo from './Fuse/components/LoginLogo';
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import getMuiTheme from 'material-ui/styles/getMuiTheme';
import {Card, CardActions} from 'material-ui/Card';
import CircularProgress from 'material-ui/CircularProgress';
import RaisedButton from 'material-ui/RaisedButton';
import Snackbar from 'material-ui/Snackbar';
import TextField from 'material-ui/TextField';
import {cyan500, pinkA200} from 'material-ui/styles/colors';

const styles = {
    main: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: `url(app.jpg)`,
        backgroundSize: 'cover',
        overflow: 'hidden'
    },
    loginCard: {float: 'left', minWidth: 300},
    chooseCard: {minWidth: 320, maxWidth: 500},
    form: {padding: '0 1em 1em 1em',},
    input: {display: 'flex', margin: '0 0 8px 0'},
    hint: {
        textAlign: 'center',
        marginTop: '1em',
        color: '#ccc',
    },
    loader: {
        display: 'block',
        margin: '10px auto 10px auto',
    }
};

function getColorsFromTheme(theme) {
    if (!theme) return {
        primary1Color: cyan500,
        accent1Color: pinkA200
    };
    const {
        palette: {
            primary1Color,
            accent1Color,
        },
    } = theme;
    return {primary1Color, accent1Color};
}

/**
 * Login Input fields
 * http://redux-form.com/6.4.3/examples/material-ui/
 *
 * @param touched
 * @param error
 * @param inputProps
 * @param props
 * @return {*}
 */
const renderInput = ({meta: {touched, error} = {}, input: {...inputProps}, ...props}) =>
    <TextField
        errorText={touched && error}
        {...inputProps}
        {...props}
        fullWidth
    />;

/**
 * Login
 */
class Login extends Component {
    constructor(props) {
        super(props);
        // If Login page is loaded from a Keycloak redirect, process and set
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        this.state = {
            displayTokenCheck: false,
            notificationMessage: '',
            notificationOpen: false,
            notificationType: 'error',
            loginScreen: (code) ? 'keycloak' : 'login',
            token: null,
            loggedInUser: null,
            useKeycloak: !!(code)
        };
        this.loginSucceeded = this.loginSucceeded.bind(this);
    }

    componentDidMount() {
        if (this.state.useKeycloak) {
            // If the Login page loaded from a Keycloak redirect, fetch token
            this.postKeycloakLoginFromUrl();
        }
    }

    /**
     * Post to Keycloak with code, then check for exchange token
     */
    postKeycloakLoginFromUrl = () => {
        localStorage.removeItem('kc_id_token');
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        if (code) {
            removeSearchQuery();
            let codeVerifier = sessionStorage.getItem('code_verifier');
            if (!codeVerifier) {
                codeVerifier = localStorage.getItem('code_verifier');
                sessionStorage.setItem('code_verifier', codeVerifier);
            }
            const params = new URLSearchParams({
                client_id: 'user',
                grant_type: 'authorization_code',
                code: code,
                redirect_uri: window.location.origin + '/#/login',
                code_verifier: codeVerifier,
            });
            const tokenUrl = getKeycloakTokenUrl();
            axios.post(tokenUrl, params, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            }).then((response) => {
                if (response.data && response.data.access_token) {
                    localStorage.setItem('kc_token', response.data.access_token);
                    localStorage.setItem('kc_id_token', response.data.id_token);
                    this.postExchangeToken();
                }
            }).catch((error) => {
                console.log('Keycloak login error: ', error);
            });
        }
    };

    /**
     * Post to KTX to exchange Keycloak token for a CloudSent token
     */
    postExchangeToken = () => {
        const endpoint = Config.fuse.api.EXCHANGE_ENDPOINT;
        const exchangeURL = (isLocalhost) ? Config.fuse.api.LOCAL_AUTH_URI + endpoint : endpoint;
        const exchangeParams = new URLSearchParams({
            jwt_token: localStorage.getItem('kc_token')
        });
        axios.post(exchangeURL, exchangeParams, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        }).then((response) => {
            if (response.data && response.data.token) {
                // Extract User from token
                const token = response.data.token;
                const decodedToken = decodeJWTToken(token);
                const user = (decodedToken.payload && decodedToken.payload.data) ? decodedToken.payload.data : null;
                if (user.locked === true) {
                    this.loginFailed('Login failed: This User is locked.');
                } else if (user.active === false) {
                    this.loginFailed('Login failed: This User is inactive.');
                } else {
                    this.loginSucceeded(user);
                    // Store the CloudSent token for subsequent calls
                    localStorage.setItem('token', token);
                    localStorage.removeItem('kc_token');
                    localStorage.setItem('auth_method', 'kc');
                    localStorage.removeItem('code_verifier');
                    // Load the TokenCheck
                    this.setState({
                        displayTokenCheck: true,
                        token: token
                    });
                }
            }
        }).catch((error) => {
            console.log('exchange token error: ', error);
        });
    };

    /**
     * Post to authorization service to receive a token
     *
     * @param email
     * @param password
     */
    postCloudSentLogin = ({email, password}) => {
        // Set Authorization URI to relative unless developing locally
        const endpoint = Config.fuse.api.AUTH_ENDPOINT;
        const uri = (isLocalhost) ? Config.fuse.api.LOCAL_AUTH_URI + endpoint : endpoint;
        const payload = {
            "payload": {
                "systemUserDataModel": {
                    email: email,
                    password: password
                }
            }
        };
        localStorage.removeItem('kc_id_token');

        // Post the login and store user data
        axios
            .post(uri, payload)
            .then((result) => {
                if (result.data.payload) {
                    // Set the token and user object if available
                    const token = result.data.payload.token;
                    const user = result.data.payload.user;
                    if (token) {
                        // Check for user data
                        if (token && user) {
                            // Reject locked or inactive users
                            if (user.locked === true) {
                                this.loginFailed('Login failed: This User is locked.');
                            } else if (user.active === false) {
                                this.loginFailed('Login failed: This User is inactive.');
                            } else {
                                this.loginSucceeded(user);
                                // Store the token for all subsequent calls
                                localStorage.setItem('token', token);
                                localStorage.setItem('auth_method', 'cs');
                                // Load the TokenCheck
                                this.setState({
                                    displayTokenCheck: true,
                                    token: token
                                });
                            }
                        } else {
                            this.loginFailed('Login failed: Incorrect credentials.');
                        }
                    }
                } else {
                    this.loginFailed('Login failed: No token available');
                }
            })
            .catch((error) => {
                if (error.response) {
                    // Server response outside 2xx
                    let payload = error.response.data.payload;
                    let message = error.response.data.message;
                    let error_message = error.response.data.error_message;

                    if (payload && payload.indexOf('password') !== -1) {
                        this.loginFailed('Incorrect credentials.');
                    } else if (message) {
                        this.loginFailed(message);
                    } else if (error_message) {
                        this.loginFailed(error_message);
                    } else {
                        this.loginFailed('Login failed.');
                    }
                } else if (error.request) {
                    // No response
                    console.log('Request Error: ' + error.request);
                    this.loginFailed('No response from server.');
                } else {
                    console.log('Error: ', error.message);
                    this.loginFailed('No response from server.');
                }
            });
    };

    /**
     * Generate a challenge from Keycloak Login click
     */
    handleKeycloakLoginClick = () => {
        const codeVerifier = createCodeVerifier();
        localStorage.removeItem('kc_id_token');
        generateCodeChallenge(codeVerifier).then((codeChallenge) => {
            window.location.href = getKeycloakAuthUrl(codeChallenge, true);
        });
    };

    /**
     * Process successful login
     */
    loginSucceeded = (user) => {
        sessionStorage.setItem('token_state', 'valid');
        if (this.state.loginScreen === 'login' || this.state.loginScreen === 'keycloak') {
            // Check to assure this user is a Sysop with access to System Admin
            if (user.sys_op === true) {
                // Switch to select Systems screen
                this.setState({
                    loggedInUser: user,
                    systemList: user.systems,
                    notificationMessage: 'Logged in successfully',
                    notificationType: 'message',
                    notificationOpen: true
                });
                const {userLogin, location} = this.props;
                userLogin({user}, location.state ? location.state.nextPathname : '/');
            } else {
                // Display No Access screen
                this.setState({
                    loginScreen: 'no-access',
                    loggedInUser: user,
                    notificationMessage: 'No access to System Admin',
                    notificationType: 'error',
                    notificationOpen: true
                });
            }
        }
    };

    /**
     * Display error notification
     */
    loginFailed = (message) => {
        sessionStorage.setItem('token_state', 'invalid');
        this.setState({
            notificationMessage: message,
            notificationType: 'error',
            notificationOpen: true
        });
    };

    /**
     * Handle initial login from form
     *
     * @param email
     * @param password
     */
    initialLogin = ({email, password}) => {
        this.postCloudSentLogin({email, password});
    };

    /**
     * Logout after No Access screen
     */
    logoutKeycloak = () => {
        clearUser();
        window.location.href = getKeycloakLogoutUrl();
    };

    render() {
        const {
            displayTokenCheck,
            loginScreen,
            notificationMessage,
            notificationOpen,
            notificationType,
            token
        } = this.state;

        const {
            handleSubmit,
            submitting,
            theme
        } = this.props;

        const muiTheme = getMuiTheme(theme);
        const {primary1Color, accent1Color} = getColorsFromTheme(muiTheme);
        const notificationStyle = {};
        if (notificationType === 'error') {
            notificationStyle.backgroundColor = accent1Color;
        }
        if (notificationType === 'message') {
            notificationStyle.backgroundColor = primary1Color;
        }

        return (
            <MuiThemeProvider muiTheme={muiTheme}>
                <div style={{...styles.main, backgroundColor: primary1Color}}>

                    {loginScreen === 'login' &&
                    <div>
                        <Card style={styles.loginCard}>
                            <LoginLogo/>
                            <form onSubmit={handleSubmit(this.initialLogin)}>
                                <div style={styles.form}>
                                    <p style={styles.hint}>Welcome to CloudSent POS System Operations</p>
                                    <div style={styles.input}>
                                        <Field
                                            name="email"
                                            type="email"
                                            component={renderInput}
                                            floatingLabelText="Email"
                                            validate={[email]}
                                        />
                                    </div>
                                    <div style={styles.input}>
                                        <Field
                                            name="password"
                                            component={renderInput}
                                            floatingLabelText="Password"
                                            type="password"
                                        />
                                    </div>
                                    <RaisedButton
                                        type="submit"
                                        primary
                                        disabled={submitting}
                                        label="Sign In"
                                        fullWidth
                                    />
                                    <p style={styles.hint}>
                                        Or log in with CloudSent SSO:
                                    </p>
                                    <RaisedButton
                                        type="button"
                                        primary
                                        disabled={submitting}
                                        label="Login with CloudSent SSO"
                                        fullWidth
                                        onClick={this.handleKeycloakLoginClick}
                                    />
                                </div>
                            </form>
                        </Card>
                    </div>
                    }

                    {loginScreen === 'keycloak' &&
                        <Card style={styles.chooseCard}>
                            <LoginLogo/>
                            <div style={styles.form}>
                                <CircularProgress
                                    size={36}
                                    thickness={3}
                                    style={styles.loader}
                                />
                            </div>
                        </Card>
                    }

                    {loginScreen === 'no-access' &&
                        <Card style={styles.chooseCard}>
                            <LoginLogo/>
                            <div style={styles.form}>
                                <p style={styles.hint}>Sorry, this account does not have access to CloudSent POS
                                    Operations.</p>
                            </div>
                        <CardActions>
                            <RaisedButton
                                type="button"
                                primary
                                onClick={this.logoutKeycloak}
                                disabled={submitting}
                                label="Logout"
                                fullWidth
                            />
                        </CardActions>
                    </Card>
                    }
                    <Snackbar
                        open={notificationOpen}
                        message={notificationMessage}
                        autoHideDuration={3000}
                        bodyStyle={notificationStyle}
                    />
                    {displayTokenCheck && token &&
                        <TokenCheck key="LoginTokenCheck" name="LoginTokenCheck" token={token}/>
                    }
                </div>
            </MuiThemeProvider>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    let form = state.form['signIn'];
    if (form && form.values) {
        const emailValue = (form.values['email']) ? form.values['email'].toString() : null;
        const passwordValue = (form.values['password']) ? form.values['password'].toString() : null;
        return {
            emailValue: emailValue,
            passwordValue: passwordValue,
        }
    }
    return {
        emailValue: null,
        passwordValue: null
    }
};

Login.propTypes = {
    ...propTypes,
    authClient: PropTypes.func,
    emailValue: PropTypes.string,
    loggedInUser: PropTypes.object,
    loginScreen: PropTypes.string,
    passwordValue: PropTypes.string,
    previousRoute: PropTypes.string,
    theme: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
};

Login.defaultProps = {
    emailValue: null,
    loggedInUser: null,
    loginScreen: 'login',
    passwordValue: null,
    theme: {},
};

const enhance = compose(
    translate,
    reduxForm({
        form: 'signIn',
        validate: (values, props) => {
            const errors = {};
            const {translate} = props;
            if (!values.email) errors.email = translate('aor.validation.required');
            if (!values.password) errors.password = translate('aor.validation.required');
            return errors;
        },
    }),
    connect(
        mapStateToProps,
        {userLogin: userLoginAction}
    ),
);

export default enhance(Login);



// WEBPACK FOOTER //
// ./src/Login.js